import { AnimatePresence, motion } from 'framer-motion';
import { CSSProperties, FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import {
  BriefcaseIcon,
  CapQuestLogo,
  CoinsStackedIcon,
  CrossIcon,
  DataIcon,
  FolderIcon,
  GiftIcon,
  HeartIcon,
  LightningIcon,
  MenuIcon,
  PieChartIcon,
  PlayIcon,
} from '../../assets/icons';
import promoPhoto from '../../assets/images/header-promo.png';
import { useScreenSize } from '../../hooks/use-screen-size.hook';

const promoVideo = process.env.REACT_APP_PROMO_VIDEO_URL || '/videos/cap-quest-v04.mp4';
import { SolutionVariant } from '../../pages/solutions/solutions-types';
import Button from '../button';
import { DropDown } from '../dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '../navigation-menu';
import { RootPortal } from '../root-portal';

type HeaderProps = {
  className?: string;
  onHoverChange?: (state: boolean) => void;
  style?: CSSProperties;
  textClassName?: string;
};

export const Header: FC<HeaderProps> = () => {
  const [isBurgerMenuOpen, setBurgerMenuOpen] = useState(false);
  const [isModalWithVideoOpen, setModalWithVideoState] = useState(false);
  const { isMobile } = useScreenSize();
  const navigate = useNavigate();
  useEffect(() => {
    setBurgerMenuOpen(false);
  }, [isMobile]);

  return (
    <>
      {isModalWithVideoOpen && (
        <RootPortal>
          <div
            className="fixed inset-0 z-50 flex cursor-pointer items-center justify-center bg-black/70"
            onClick={() => setModalWithVideoState(false)}
          >
            <div
              className="relative w-[80%] max-w-4xl overflow-hidden rounded-2xl bg-black shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                autoPlay
                className="h-full w-full object-cover"
                controls
                muted
                onEnded={() => setModalWithVideoState(false)}
                playsInline
                ref={(el) => {
                  if (!el) return;
                  el.volume = 0.1;
                  el.muted = false;
                  el.play().catch(() => {});
                }}
                src={promoVideo}
              />
            </div>
          </div>
        </RootPortal>
      )}

      <div className="relative">
        <NavigationMenu className="flex w-screen justify-center border-b border-b-[#1018280F] px-6">
          <NavigationMenuList>
            <NavigationMenuLink className="z-20 flex cursor-pointer items-center" href="/">
              <CapQuestLogo mode="full" />
            </NavigationMenuLink>

            {!isMobile && (
              <>
                <NavigationMenuItem>
                  <NavigationMenuTrigger>Solutions</NavigationMenuTrigger>

                  <NavigationMenuContent className="flex gap-4">
                    <div className="w/full grid grid-flow-col grid-rows-2 gap-4 px-6 py-10">
                      <NavigationMenuLink
                        className="flex w-[305px] shrink-0 gap-4 rounded-md transition-colors"
                        href={`/solutions/${SolutionVariant.EQUITY_PLAN_AUTOMATION}`}
                      >
                        <DataIcon className="mt-1" />
                        <div className="flex flex-col gap-4 text-start">
                          <span className="text-base font-semibold text-gray-900">
                            ESOP Management
                          </span>
                          <span className="text-sm text-gray-500">
                            Eliminate paperwork and errors with automated equity mgt.
                          </span>
                        </div>
                      </NavigationMenuLink>

                      <NavigationMenuLink
                        className="flex w-[305px] shrink-0 gap-4 rounded-md transition-colors"
                        href={`/solutions/${SolutionVariant.ENHANCED_INVESTOR_RELATIONS}`}
                      >
                        <HeartIcon className="mt-1" />
                        <div className="flex flex-col gap-4 text-start">
                          <span className="text-base font-semibold text-gray-900">
                            Investor Relations
                          </span>
                          <span className="text-sm text-gray-500">
                            Build trust with real-time equity insights, empowering investors.
                          </span>
                        </div>
                      </NavigationMenuLink>

                      <NavigationMenuLink
                        className="flex w-[305px] shrink-0 gap-4 rounded-md transition-colors"
                        href={`/solutions/${SolutionVariant.OPTIMIZED_FUNDRAISING_INSTRUMENTS}`}
                      >
                        <LightningIcon className="mt-1" />
                        <div className="flex flex-col gap-4 text-start">
                          <span className="text-base font-semibold text-gray-900">
                            Financial Tools
                          </span>
                          <span className="text-sm text-gray-500">
                            Streamlines cap tables, equity, and debt tracking for founders.
                          </span>
                        </div>
                      </NavigationMenuLink>

                      <NavigationMenuLink
                        className="flex w-[305px] shrink-0 gap-4 rounded-md transition-colors"
                        href={`/solutions/${SolutionVariant.SIMPLIFIED_CAP_TABLE}`}
                      >
                        <CoinsStackedIcon className="mt-1" />
                        <div className="flex flex-col gap-4 text-start">
                          <span className="text-base font-semibold text-gray-900">
                            Cap Table Management
                          </span>
                          <span className="text-sm text-gray-500">
                            Manage cap tables effortlessly with real-time updates and reports.
                          </span>
                        </div>
                      </NavigationMenuLink>

                      <NavigationMenuLink
                        className="flex w-[305px] shrink-0 gap-4 rounded-md transition-colors"
                        href={`/solutions/${SolutionVariant.EFFORTLESS_SCENARIO_MODELING}`}
                      >
                        <CoinsStackedIcon className="mt-1" />
                        <div className="flex flex-col gap-4 text-start">
                          <span className="text-base font-semibold text-gray-900">
                            Scenario Modeling
                          </span>
                          <span className="text-sm text-gray-500">
                            Visualize impacts of financing and exits with scenario modeling.
                          </span>
                        </div>
                      </NavigationMenuLink>

                      <NavigationMenuLink className="flex w-[305px] shrink-0 gap-4 rounded-md transition-colors">
                        <FolderIcon className="mt-1 size-6" />
                        <div className="flex flex-col gap-4 text-start">
                          <div className="flex items-center gap-4">
                            <span className="text-base font-semibold text-gray-900">Data Room</span>
                            <span
                              className="rounded-[32px] border border-[#344054] px-2 text-[8px] font-semibold leading-[2.2] text-white"
                              style={{
                                background: 'linear-gradient(180deg, #344054 0%, #475467 100%)',
                              }}
                            >
                              Coming Soon
                            </span>
                          </div>
                          <span className="text-sm text-gray-500">
                            Secure document sharing for business transactions and needs.
                          </span>
                        </div>
                      </NavigationMenuLink>
                    </div>

                    <div
                      className="relative flex h-[286px] w-[361px] shrink-0 cursor-pointer items-center justify-center overflow-hidden"
                      onClick={() => setModalWithVideoState(true)}
                    >
                      <div className="absolute flex size-12 items-center justify-center rounded-full bg-black/10 backdrop-blur-sm">
                        <PlayIcon />
                      </div>
                      <img
                        alt=""
                        className="h-full w-full object-cover object-left"
                        src={promoPhoto}
                      />
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger>Partnerships</NavigationMenuTrigger>

                  <NavigationMenuContent className="flex gap-4">
                    <div className="w/full grid grid-flow-col grid-rows-1 gap-4 px-6 py-10">
                      <NavigationMenuLink
                        className="flex w-[305px] shrink-0 gap-4 rounded-md transition-colors"
                        href="/perks"
                      >
                        <GiftIcon />
                        <div className="flex flex-col gap-4 text-start">
                          <span className="text-base font-semibold text-gray-900">
                            Perks & Partnerships
                          </span>
                          <span className="text-sm text-gray-500">
                            Unlock exclusive benefits and tools to support your startup’s growth.
                          </span>
                        </div>
                      </NavigationMenuLink>
                      <NavigationMenuLink
                        className="flex w-[305px] shrink-0 gap-4 rounded-md transition-colors"
                        href="/become-a-partner"
                      >
                        <BriefcaseIcon />
                        <div className="flex flex-col gap-4 text-start">
                          <span className="text-base font-semibold text-gray-900">
                            Become a Partner
                          </span>
                          <span className="text-sm text-gray-500">
                            Join us and securely share offers tailored for startup needs.
                          </span>
                        </div>
                      </NavigationMenuLink>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuLink className={navigationMenuTriggerStyle()} href="/about">
                  About Us
                </NavigationMenuLink>
              </>
            )}
          </NavigationMenuList>

          <NavigationMenuList className="items-center gap-3">
            <NavigationMenuLink
              className={navigationMenuTriggerStyle({ className: 'max-md-plus:hidden' })}
              href="/book-demo"
            >
              Contact Sales
            </NavigationMenuLink>

            <NavigationMenuLink
              className={navigationMenuTriggerStyle({ className: 'max-md-plus:hidden' })}
              href={process.env.REACT_APP_CAP_QUEST_LOGIN_URL}
              rel="noreferrer noopener nofollow"
              target="_blank"
            >
              Log in
            </NavigationMenuLink>

            <NavigationMenuLink
              className={twMerge(
                'font-inter w-full cursor-pointer items-center justify-center text-nowrap rounded bg-brand-700 px-8 py-[14px] text-sm font-[500] text-white transition-all duration-300 hover:bg-[#146EF5] max-md-plus:hidden',
              )}
              href={process.env.REACT_APP_CAP_QUEST_REGISTER_URL}
              rel="noreferrer noopener nofollow"
              target="_blank"
            >
              Get Started
            </NavigationMenuLink>

            {isMobile && (
              <Button
                className="cursor-pointer pl-3"
                onClick={() => setBurgerMenuOpen((prev) => !prev)}
                styleType="NONE"
              >
                {isBurgerMenuOpen ? <CrossIcon /> : <MenuIcon />}
              </Button>
            )}
          </NavigationMenuList>
        </NavigationMenu>
        <AnimatePresence>
          {isMobile && isBurgerMenuOpen && (
            <RootPortal className={twMerge('fixed inset-0 z-50')}>
              <motion.div
                animate={{ opacity: 1, transition: { duration: 0.15 } }}
                className="fixed inset-0 z-40 bg-black/60"
                exit={{ opacity: 0, transition: { duration: 0.1 } }}
                initial={{ opacity: 0 }}
                onClick={() => setBurgerMenuOpen(false)}
              />

              <motion.div
                animate={{
                  y: 0,
                  opacity: 1,
                  transition: { duration: 0.22, ease: [0.16, 1, 0.3, 1] },
                }}
                className="fixed left-0 right-0 top-0 z-50"
                exit={{ y: -24, opacity: 0, transition: { duration: 0.16 } }}
                initial={{ y: -32, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <motion.div
                  animate={{ height: 'auto', transition: { duration: 0.22 } }}
                  className="overflow-hidden"
                  exit={{ height: 0, transition: { duration: 0.16 } }}
                  initial={{ height: 0 }}
                >
                  <div
                    className={twMerge(
                      'flex h-[100vh] w-full flex-col divide-y divide-[#1018280F] overflow-y-auto rounded-b-lg bg-white shadow-sm',
                    )}
                  >
                    <div className="flex h-16 w-full items-center justify-between px-6">
                      <a className="z-20 flex cursor-pointer items-center" href="/">
                        <CapQuestLogo mode="full" />
                      </a>
                      <Button
                        className="w-fit cursor-pointer"
                        onClick={() => setBurgerMenuOpen((prev) => !prev)}
                        styleType="NONE"
                      >
                        {isBurgerMenuOpen ? <CrossIcon /> : <MenuIcon />}
                      </Button>
                    </div>

                    <DropDown className="w-full px-6" title="Solutions">
                      <div className="flex flex-col items-center justify-center pb-6 pt-2">
                        <div className="flex flex-col">
                          <div className="flex flex-wrap items-center justify-center gap-y-4 xlg:items-start xlg:justify-start">
                            <div
                              className="flex w-[304px] cursor-pointer items-start justify-start gap-4 p-4 text-start"
                              onClick={() => {
                                setBurgerMenuOpen(false);
                                navigate(`/solutions/${SolutionVariant.EQUITY_PLAN_AUTOMATION}`);
                              }}
                            >
                              <DataIcon className="mt-1" />
                              <div className="flex flex-col gap-4">
                                <span className="text-base font-semibold text-gray-900">
                                  ESOP Management
                                </span>
                                <span className="text-sm text-gray-500">
                                  Eliminate paperwork and errors with automated equity mgt.
                                </span>
                              </div>
                            </div>

                            <div
                              className="flex w-[304px] cursor-pointer items-start justify-start gap-4 p-4 text-start"
                              onClick={() => {
                                setBurgerMenuOpen(false);
                                navigate(
                                  `/solutions/${SolutionVariant.OPTIMIZED_FUNDRAISING_INSTRUMENTS}`,
                                );
                              }}
                            >
                              <LightningIcon className="mt-1" />
                              <div className="flex flex-col gap-4">
                                <span className="text-base font-semibold text-gray-900">
                                  Financial Tools
                                </span>
                                <span className="text-sm text-gray-500">
                                  Streamlines cap tables, equity, and debt tracking for founders.
                                </span>
                              </div>
                            </div>

                            <div
                              className="flex w-[304px] cursor-pointer items-start justify-start gap-4 p-4 text-start"
                              onClick={() => {
                                setBurgerMenuOpen(false);
                                navigate(`/solutions/${SolutionVariant.SIMPLIFIED_CAP_TABLE}`);
                              }}
                            >
                              <CoinsStackedIcon className="mt-1" />
                              <div className="flex flex-col gap-4">
                                <span className="text-base font-semibold text-gray-900">
                                  Cap Table Management
                                </span>
                                <span className="text-sm text-gray-500">
                                  Manage cap tables effortlessly with real-time updates and reports.
                                </span>
                              </div>
                            </div>

                            <div
                              className="flex w-[304px] cursor-pointer items-start justify-start gap-4 p-4 text-start"
                              onClick={() => {
                                setBurgerMenuOpen(false);
                                navigate(
                                  `/solutions/${SolutionVariant.ENHANCED_INVESTOR_RELATIONS}`,
                                );
                              }}
                            >
                              <HeartIcon className="mt-1" />
                              <div className="flex flex-col gap-4">
                                <span className="text-base font-semibold text-gray-900">
                                  Investor Relations
                                </span>
                                <span className="text-sm text-gray-500">
                                  Build trust with real-time equity insights, empowering investors.
                                </span>
                              </div>
                            </div>

                            <div
                              className="flex w-[304px] cursor-pointer items-start justify-start gap-4 p-4 text-start"
                              onClick={() => {
                                setBurgerMenuOpen(false);
                                navigate(
                                  `/solutions/${SolutionVariant.EFFORTLESS_SCENARIO_MODELING}`,
                                );
                              }}
                            >
                              <PieChartIcon className="mt-1 size-6" />
                              <div className="flex flex-col gap-4">
                                <span className="text-base font-semibold text-gray-900">
                                  Scenario Modeling
                                </span>
                                <span className="text-sm text-gray-500">
                                  Visualize impacts of financing and exits with scenario modeling.
                                </span>
                              </div>
                            </div>

                            <div
                              className="flex w-[304px] cursor-pointer items-start justify-start gap-4 p-4 text-start"
                              onClick={() => {
                                setBurgerMenuOpen(false);
                                navigate('/solutions');
                              }}
                            >
                              <FolderIcon className="mt-1 size-6" />
                              <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-4">
                                  <span className="text-base font-semibold text-gray-900">
                                    Data Room
                                  </span>
                                  <span
                                    className="rounded-[32px] border border-[#344054] px-2 text-[8px] font-semibold leading-[2.2] text-white"
                                    style={{
                                      background:
                                        'linear-gradient(180deg, #344054 0%, #475467 100%)',
                                    }}
                                  >
                                    Coming Soon
                                  </span>
                                </div>
                                <span className="text-sm text-gray-500">
                                  Secure document sharing for business transactions and needs.
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        className="relative flex h-[286px] w-full shrink-0 cursor-pointer items-center justify-center overflow-hidden"
                        onClick={() => setModalWithVideoState(true)}
                      >
                        <div className="absolute flex size-12 items-center justify-center rounded-full bg-black/10 backdrop-blur-sm">
                          <PlayIcon />
                        </div>
                        <img
                          alt=""
                          className="h-full w-full object-cover object-left"
                          src={promoPhoto}
                        />
                      </div>
                    </DropDown>

                    <DropDown className="w-full px-6" title="Partnerships">
                      <div className="flex flex-col items-center justify-center pb-6 pt-2">
                        <div className="flex flex-col">
                          <div className="flex flex-wrap items-center justify-center gap-y-4 xlg:items-start xlg:justify-start">
                            <div
                              className="flex w-[304px] cursor-pointer items-start justify-start gap-4 p-4 text-start"
                              onClick={() => {
                                setBurgerMenuOpen(false);
                                navigate('/perks');
                              }}
                            >
                              <BriefcaseIcon />
                              <div className="flex flex-col gap-4">
                                <span className="text-base font-semibold text-gray-900">
                                  Perks & Partnerships
                                </span>
                                <span className="text-sm text-gray-500">
                                  Unlock exclusive benefits and tools to support your startup’s
                                  growth.
                                </span>
                              </div>
                            </div>

                            <div
                              className="flex w-[304px] cursor-pointer items-start justify-start gap-4 p-4 text-start"
                              onClick={() => {
                                setBurgerMenuOpen(false);
                                navigate('/become-a-partner');
                              }}
                            >
                              <GiftIcon />
                              <div className="flex flex-col gap-4">
                                <span className="text-base font-semibold text-gray-900">
                                  Become a Partner
                                </span>
                                <span className="text-sm text-gray-500">
                                  Join us and securely share offers tailored for startup needs.
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </DropDown>

                    <Button
                      className="flex w-full items-start justify-start border-b-[1px] border-[#1018280F] px-6 py-4 text-sm font-[550] text-gray-900"
                      onClick={() => {
                        navigate('/about');
                      }}
                      styleType="NONE"
                    >
                      About Us
                    </Button>

                    <div className="mt-auto flex flex-col gap-4 border-t-[1px] border-[#1018280F] px-4 py-6 xlg:hidden">
                      <div className="flex items-center gap-3">
                        <a
                          className="font-inter flex w-full cursor-pointer items-center justify-center text-nowrap rounded border-[1px] border-gray-300 bg-transparent px-4 py-[14px] text-sm font-[500] text-gray-900 transition-all duration-300 hover:bg-[white]"
                          href={process.env.REACT_APP_CAP_QUEST_LOGIN_URL}
                          rel="noreferrer noopener nofollow"
                          target="_blank"
                        >
                          Log in
                        </a>

                        <Button
                          className="flex w-full items-center justify-center border-[1px] border-gray-300 px-4 py-4 text-sm font-[550] text-gray-900"
                          onClick={() => {
                            setBurgerMenuOpen(false);
                            window.location.assign('/book-demo');
                          }}
                          styleType="NONE"
                        >
                          Contact Sales
                        </Button>
                      </div>

                      <a
                        className="w/full font-inter flex cursor-pointer items-center justify-center text-nowrap rounded bg-brand-700 px-8 py-[14px] text-sm font-[500] text-white transition-all duration-300 hover:bg-[#146EF5]"
                        href={process.env.REACT_APP_CAP_QUEST_REGISTER_URL}
                        rel="noreferrer noopener nofollow"
                        target="_blank"
                      >
                        Get Started
                      </a>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </RootPortal>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};
